import React, { useEffect, useState, useRef } from 'react';
import { XIcon, MailIcon, PhoneIcon, MusicIcon, CheckCircleIcon, PencilIcon, GlobeIcon, HomeIcon, ClockIcon, MapPinIcon, UserIcon, SaveIcon, Trash2Icon, AlertCircleIcon } from 'lucide-react';
import { formatPhoneNumber, normalizePhoneNumber, getFormattedCursorPosition } from '../../utils/phoneUtils';
import { validateEmail, validatePhone } from '../../utils/validationUtils';
import { useAuth } from '../../contexts/AuthContext';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../../firebase';
import { deleteStudentAccount } from '../../services/studentService';
import toast from 'react-hot-toast';
interface Student {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  instrument?: string;
  instruments?: string[];
  grade?: number;
  notes?: string;
  avatar?: string;
  profilePicture?: string;
  status?: string;
  lessonType?: 'In-person' | 'Online' | 'Hybrid';
  timeZone?: string;
  address?: {
    street?: string;
    apt?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  guardian?: {
    name?: string;
    email?: string;
    phone?: string;
    preferredContact?: string;
    isEmergencyContact?: boolean;
  };
  upcomingLessonsCount?: number;
  pendingTasksCount?: number;
  lastReportDate?: string;
}
interface ViewStudentProfilePanelProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  darkMode: boolean;
  onEdit?: (studentId: string, updatedData: any) => void;
  onViewFullProfile?: (studentId: string) => void;
  onStudentDeleted?: () => void;
}
export const ViewStudentProfilePanel: React.FC<ViewStudentProfilePanelProps> = ({
  isOpen,
  onClose,
  student,
  darkMode,
  onEdit,
  onViewFullProfile,
  onStudentDeleted
}) => {
  const {
    currentUser
  } = useAuth();
  // Single edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // NEW: Add displayStudent state for optimistic updates
  const [displayStudent, setDisplayStudent] = useState<Student | null>(null);
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0
  });
  // Edit state for all fields
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [editedInstrument, setEditedInstrument] = useState('');
  const [editedGrade, setEditedGrade] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedLessonType, setEditedLessonType] = useState<'In-person' | 'Online' | 'Hybrid' | ''>('');
  const [editedTimeZone, setEditedTimeZone] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  // Address edit state
  const [editedAddressStreet, setEditedAddressStreet] = useState('');
  const [editedAddressApt, setEditedAddressApt] = useState('');
  const [editedAddressCity, setEditedAddressCity] = useState('');
  const [editedAddressState, setEditedAddressState] = useState('');
  const [editedAddressZip, setEditedAddressZip] = useState('');
  // Guardian edit state
  const [editedGuardianName, setEditedGuardianName] = useState('');
  const [editedGuardianEmail, setEditedGuardianEmail] = useState('');
  const [editedGuardianPhone, setEditedGuardianPhone] = useState('');
  const [editedGuardianPreferredContact, setEditedGuardianPreferredContact] = useState('');
  const [editedGuardianIsEmergencyContact, setEditedGuardianIsEmergencyContact] = useState(false);
  // Refs for phone inputs to manage cursor position
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const guardianPhoneInputRef = useRef<HTMLInputElement>(null);
  // Delete student states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  // NEW: Validation error states
  const [validationErrors, setValidationErrors] = useState({
    email: null as string | null,
    phone: null as string | null,
    guardianEmail: null as string | null,
    guardianPhone: null as string | null
  });
  // NEW: Sync displayStudent with student prop changes
  useEffect(() => {
    setDisplayStudent(student);
  }, [student]);
  useEffect(() => {
    if (isOpen && student) {
      // Initialize all edit fields with current student data
      setEditedFirstName(student.firstName || '');
      setEditedLastName(student.lastName || '');
      setEditedInstrument(student.instrument || (student.instruments && student.instruments.length > 0 ? student.instruments[0] : ''));
      setEditedGrade(student.grade ? student.grade.toString() : '');
      setEditedEmail(student.email || '');
      // Format phone for display
      setEditedPhone(formatPhoneNumber(student.phone || ''));
      setEditedLessonType(student.lessonType || '');
      setEditedTimeZone(student.timeZone || '');
      setEditedNotes(student.notes || '');
      // Initialize address fields
      setEditedAddressStreet(student.address?.street || '');
      setEditedAddressApt(student.address?.apt || '');
      setEditedAddressCity(student.address?.city || '');
      setEditedAddressState(student.address?.state || '');
      setEditedAddressZip(student.address?.zip || '');
      // Initialize guardian fields - format guardian phone for display
      setEditedGuardianName(student.guardian?.name || '');
      setEditedGuardianEmail(student.guardian?.email || '');
      setEditedGuardianPhone(formatPhoneNumber(student.guardian?.phone || ''));
      setEditedGuardianPreferredContact(student.guardian?.preferredContact || '');
      setEditedGuardianIsEmergencyContact(student.guardian?.isEmergencyContact || false);
      // Reset edit mode and success message
      setIsEditMode(false);
      setSaveSuccess(false);
      // Reset position when panel opens
      setPosition({
        x: 0,
        y: 0
      });
      setIsDragging(false);
      // Reset delete modal state
      setIsDeleteModalOpen(false);
      setDeletePassword('');
      setPasswordError('');
      // Reset validation errors
      setValidationErrors({
        email: null,
        phone: null,
        guardianEmail: null,
        guardianPhone: null
      });
    }
  }, [isOpen, student]);
  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging from the header, not from buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y
    };
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;
      setPosition({
        x: dragRef.current.initialX + deltaX,
        y: dragRef.current.initialY + deltaY
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  // Special handler for student phone input with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const oldValue = editedPhone;
    const oldCursor = input.selectionStart || 0;
    const rawValue = input.value;
    // Format the phone number
    const formatted = formatPhoneNumber(rawValue);
    // Update state
    setEditedPhone(formatted);
    // Clear validation error when user types
    if (validationErrors.phone) {
      setValidationErrors(prev => ({
        ...prev,
        phone: null
      }));
    }
    // Restore cursor position after React re-renders
    // FIX: Use rawValue (not oldValue) because oldCursor is the cursor position
    // in the NEW input value (rawValue), not the previous state (oldValue).
    // Using oldValue caused digit-position mismatch and scrambled phone numbers.
    requestAnimationFrame(() => {
      if (phoneInputRef.current) {
        const newCursor = getFormattedCursorPosition(rawValue, formatted, oldCursor);
        phoneInputRef.current.setSelectionRange(newCursor, newCursor);
      }
    });
  };
  // Special handler for guardian phone input with formatting
  const handleGuardianPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const oldValue = editedGuardianPhone;
    const oldCursor = input.selectionStart || 0;
    const rawValue = input.value;
    // Format the phone number
    const formatted = formatPhoneNumber(rawValue);
    // Update state
    setEditedGuardianPhone(formatted);
    // Clear validation error when user types
    if (validationErrors.guardianPhone) {
      setValidationErrors(prev => ({
        ...prev,
        guardianPhone: null
      }));
    }
    // Restore cursor position after React re-renders
    // FIX: Use rawValue (not oldValue) because oldCursor is the cursor position
    // in the NEW input value (rawValue), not the previous state (oldValue).
    requestAnimationFrame(() => {
      if (guardianPhoneInputRef.current) {
        const newCursor = getFormattedCursorPosition(rawValue, formatted, oldCursor);
        guardianPhoneInputRef.current.setSelectionRange(newCursor, newCursor);
      }
    });
  };
  // NEW: Validation handlers for email fields
  const handleEmailBlur = () => {
    const validation = validateEmail(editedEmail);
    setValidationErrors(prev => ({
      ...prev,
      email: validation.error
    }));
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedEmail(e.target.value);
    // Clear error when user types
    if (validationErrors.email) {
      setValidationErrors(prev => ({
        ...prev,
        email: null
      }));
    }
  };
  const handleGuardianEmailBlur = () => {
    const validation = validateEmail(editedGuardianEmail);
    setValidationErrors(prev => ({
      ...prev,
      guardianEmail: validation.error
    }));
  };
  const handleGuardianEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedGuardianEmail(e.target.value);
    // Clear error when user types
    if (validationErrors.guardianEmail) {
      setValidationErrors(prev => ({
        ...prev,
        guardianEmail: null
      }));
    }
  };
  // NEW: Validation handlers for phone fields
  const handlePhoneBlur = () => {
    const validation = validatePhone(editedPhone);
    setValidationErrors(prev => ({
      ...prev,
      phone: validation.error
    }));
  };
  const handleGuardianPhoneBlur = () => {
    const validation = validatePhone(editedGuardianPhone);
    setValidationErrors(prev => ({
      ...prev,
      guardianPhone: validation.error
    }));
  };
  const handleEnterEditMode = () => {
    setIsEditMode(true);
    setSaveSuccess(false);
  };
  const handleSaveAll = () => {
    if (onEdit && student) {
      // Validate names
      const trimmedFirstName = editedFirstName.trim();
      const trimmedLastName = editedLastName.trim();
      if (!trimmedFirstName) {
        toast.error('First name is required.');
        return;
      }
      // Validate name pattern (letters, spaces, hyphens, apostrophes)
      const namePattern = /^[a-zA-Z\s'-]+$/;
      if (!namePattern.test(trimmedFirstName)) {
        toast.error('First name can only contain letters, spaces, hyphens, and apostrophes.');
        return;
      }
      if (trimmedLastName && !namePattern.test(trimmedLastName)) {
        toast.error('Last name can only contain letters, spaces, hyphens, and apostrophes.');
        return;
      }
      // Validate grade if provided
      if (editedGrade.trim()) {
        const gradeNum = parseInt(editedGrade);
        if (isNaN(gradeNum) || gradeNum < 1 || gradeNum > 12) {
          toast.error('Grade must be a number between 1 and 12.');
          return;
        }
      }
      // NEW: Validate email and phone fields before saving
      const emailValidation = validateEmail(editedEmail);
      const phoneValidation = validatePhone(editedPhone);
      const guardianEmailValidation = validateEmail(editedGuardianEmail);
      const guardianPhoneValidation = validatePhone(editedGuardianPhone);
      // Update validation errors state
      setValidationErrors({
        email: emailValidation.error,
        phone: phoneValidation.error,
        guardianEmail: guardianEmailValidation.error,
        guardianPhone: guardianPhoneValidation.error
      });
      // Check if any validation failed
      if (!emailValidation.isValid || !phoneValidation.isValid || !guardianEmailValidation.isValid || !guardianPhoneValidation.isValid) {
        toast.error('Please fix validation errors before saving.');
        return;
      }
      // Create full name for display
      const fullName = `${trimmedFirstName} ${trimmedLastName}`;
      // Normalize phone numbers to digits-only for storage
      const normalizedPhone = normalizePhoneNumber(editedPhone);
      const normalizedGuardianPhone = normalizePhoneNumber(editedGuardianPhone);
      // Prepare update payload with all edited fields
      const updatePayload = {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        name: fullName,
        instrument: editedInstrument.trim() || undefined,
        grade: editedGrade.trim() ? parseInt(editedGrade) : undefined,
        email: editedEmail,
        phone: normalizedPhone,
        lessonType: editedLessonType || undefined,
        timeZone: editedTimeZone || undefined,
        notes: editedNotes,
        address: {
          street: editedAddressStreet.trim() || undefined,
          apt: editedAddressApt.trim() || undefined,
          city: editedAddressCity.trim() || undefined,
          state: editedAddressState.trim() || undefined,
          zip: editedAddressZip.trim() || undefined
        },
        guardian: {
          name: editedGuardianName.trim() || undefined,
          email: editedGuardianEmail.trim() || undefined,
          phone: normalizedGuardianPhone,
          preferredContact: editedGuardianPreferredContact || undefined,
          isEmergencyContact: editedGuardianIsEmergencyContact
        }
      };
      console.log('ViewStudentProfilePanel: Saving all changes:', updatePayload);
      // Call parent's onEdit handler
      onEdit(student.id, updatePayload);
      // ✅ NEW: Optimistically update displayStudent immediately
      const optimisticStudent: Student = {
        ...student,
        ...updatePayload
      };
      setDisplayStudent(optimisticStudent);
      setIsEditMode(false);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }
  };
  const handleCancelEdit = () => {
    if (student) {
      // Reset all fields to original values
      setEditedFirstName(student.firstName || '');
      setEditedLastName(student.lastName || '');
      setEditedInstrument(student.instrument || (student.instruments && student.instruments.length > 0 ? student.instruments[0] : ''));
      setEditedGrade(student.grade ? student.grade.toString() : '');
      setEditedEmail(student.email || '');
      // Format phone for display when canceling
      setEditedPhone(formatPhoneNumber(student.phone || ''));
      setEditedLessonType(student.lessonType || '');
      setEditedTimeZone(student.timeZone || '');
      setEditedNotes(student.notes || '');
      // Reset address fields
      setEditedAddressStreet(student.address?.street || '');
      setEditedAddressApt(student.address?.apt || '');
      setEditedAddressCity(student.address?.city || '');
      setEditedAddressState(student.address?.state || '');
      setEditedAddressZip(student.address?.zip || '');
      // Reset guardian fields - format guardian phone for display
      setEditedGuardianName(student.guardian?.name || '');
      setEditedGuardianEmail(student.guardian?.email || '');
      setEditedGuardianPhone(formatPhoneNumber(student.guardian?.phone || ''));
      setEditedGuardianPreferredContact(student.guardian?.preferredContact || '');
      setEditedGuardianIsEmergencyContact(student.guardian?.isEmergencyContact || false);
    }
    setIsEditMode(false);
    setSaveSuccess(false);
  };
  const handleMessage = () => {
    if (onViewFullProfile && student) {
      onViewFullProfile(student.id);
      onClose();
    }
  };
  const handleAssignTask = () => {
    if (onAssignTask && student) {
      onAssignTask(student.id);
      onClose();
    }
  };
  const handleScheduleLesson = () => {
    if (onScheduleLesson && student) {
      onScheduleLesson(student.id);
      onClose();
    }
  };
  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
    setPasswordError('');
    setDeletePassword('');
  };
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletePassword('');
    setPasswordError('');
  };
  const handleDeleteConfirm = async () => {
    if (!currentUser || !student) {
      setPasswordError('Unable to authenticate. Please try again.');
      return;
    }
    // Validate password is not empty
    if (!deletePassword.trim()) {
      setPasswordError('Password is required to confirm deletion.');
      return;
    }
    setIsDeleting(true);
    setPasswordError('');
    try {
      // Step 1: Reauthenticate the user with their password
      const credential = EmailAuthProvider.credential(currentUser.email!, deletePassword);
      try {
        await reauthenticateWithCredential(auth.currentUser!, credential);
      } catch (authError: any) {
        console.error('Authentication error:', authError);
        // Handle specific authentication errors
        if (authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
          setPasswordError('Incorrect password. Please try again.');
        } else if (authError.code === 'auth/too-many-requests') {
          setPasswordError('Too many attempts. Please try again later.');
        } else {
          setPasswordError('Authentication failed. Please try again.');
        }
        setIsDeleting(false);
        return;
      }
      // Step 2: If authentication succeeds, delete the student
      const studentId = student.id;
      await deleteStudentAccount(studentId, currentUser.uid);
      // Step 3: Success - close modals and show success message
      setIsDeleteModalOpen(false);
      setDeletePassword('');
      // Show success toast
      toast.success('Student profile deleted successfully.', {
        duration: 4000,
        icon: '✅'
      });
      // Step 4: Close the profile panel
      onClose();
      // Step 5: Notify parent component to refresh student list
      if (onStudentDeleted) {
        onStudentDeleted();
      }
      // Also dispatch a custom event for other components that might be listening
      const deleteEvent = new CustomEvent('studentDeleted', {
        detail: {
          studentId
        }
      });
      window.dispatchEvent(deleteEvent);
    } catch (error: any) {
      console.error('Error deleting student:', error);
      // Handle specific deletion errors
      let errorMessage = 'Failed to delete student. Please try again.';
      if (error.message) {
        if (error.message.includes('not found')) {
          errorMessage = 'Student not found. They may have already been deleted.';
        } else if (error.message.includes('permission')) {
          errorMessage = 'Permission denied. Please check your account permissions.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      }
      toast.error(errorMessage, {
        duration: 5000,
        icon: '❌'
      });
      setIsDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };
  // Handler for Enter key in password field
  const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isDeleting && deletePassword.trim()) {
      e.preventDefault();
      handleDeleteConfirm();
    }
  };
  if (!isOpen || !displayStudent) return null;
  // Get full name with safe fallback
  const fullName = displayStudent.name || (displayStudent.firstName && displayStudent.lastName ? `${displayStudent.firstName} ${displayStudent.lastName}` : displayStudent.firstName || displayStudent.lastName || 'Unnamed Student');
  // Get instrument - prefer singular, fallback to first of array, or empty
  const displayInstrument = displayStudent.instrument || (displayStudent.instruments && displayStudent.instruments.length > 0 ? displayStudent.instruments[0] : '');
  // Get status badge styling
  const getStatusBadgeClass = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return darkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-500/10 text-green-600 border border-green-500/30';
      case 'trial':
        return darkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-500/10 text-blue-600 border border-blue-500/30';
      case 'inactive':
      case 'archived':
        return darkMode ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'bg-gray-500/10 text-gray-600 border border-gray-500/30';
      default:
        return darkMode ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'bg-gray-500/10 text-gray-600 border border-gray-500/30';
    }
  };
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-2195">
      {/* Add style tag to fix autocomplete background */}
      <style data-id="element-2196">{`
        /* Prevent autocomplete from changing background color in dark mode */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${darkMode ? 'rgba(10, 17, 40, 0.5)' : 'white'} inset !important;
          -webkit-text-fill-color: ${darkMode ? 'white' : '#111827'} !important;
          caret-color: ${darkMode ? 'white' : '#111827'} !important;
        }
        /* Firefox autocomplete fix */
        input:-moz-autofill,
        input:-moz-autofill:hover,
        input:-moz-autofill:focus {
          background-color: ${darkMode ? 'rgba(10, 17, 40, 0.5)' : 'white'} !important;
          color: ${darkMode ? 'white' : '#111827'} !important;
        }
      `}</style>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} data-id="element-2197" />
      {/* Centered Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none" data-id="element-2198">
        <div className={`w-full max-w-xl transform transition-all duration-300 ease-in-out pointer-events-auto ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`} style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        transition: isDragging ? 'none' : 'transform 0.3s ease-in-out'
      }} data-id="element-2199">
          <div className={`flex flex-col max-h-[90vh] ${darkMode ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50' : 'bg-white border border-gray-200'} shadow-xl rounded-2xl overflow-hidden`} data-id="element-2200">
            {/* Header - Draggable Area */}
            <div className={`px-6 py-5 flex items-center justify-between border-b ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} select-none`} onMouseDown={handleMouseDown} data-id="element-2201">
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2202">
                Student Profile
              </h2>
              <button onClick={onClose} className={`rounded-lg p-1.5 transition-colors cursor-pointer ${darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} data-id="element-2203">
                <XIcon className="h-5 w-5" data-id="element-2204" />
              </button>
            </div>
            {/* Scrollable Content */}
            <div className={`flex-1 overflow-y-auto px-6 py-6 space-y-6 student-profile-scrollbar ${darkMode ? 'dark-mode' : ''}`} data-id="element-2205">
              {/* Success Message */}
              {saveSuccess && <div className={`p-3 rounded-lg flex items-center gap-2 ${darkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-700 border border-green-200'}`} data-id="element-2206">
                  <CheckCircleIcon className="h-5 w-5" data-id="element-2207" />
                  <span className="text-sm font-medium" data-id="element-2208">
                    Changes saved successfully!
                  </span>
                </div>}
              {/* Name Section with Avatar, Name, Status, and Instrument */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2209">
                <div data-id="element-2210">
                  {/* Name and Instrument */}
                  <div className="flex-1 min-w-0" data-id="element-2211">
                    {isEditMode ? <div className="space-y-3" data-id="element-2212">
                        {/* First Name Input */}
                        <div data-id="element-2213">
                          <label htmlFor="firstName" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2214">
                            First Name
                          </label>
                          <input id="firstName" type="text" value={editedFirstName} onChange={e => setEditedFirstName(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="First Name" data-id="element-2215" />
                        </div>
                        {/* Last Name Input */}
                        <div data-id="element-2216">
                          <label htmlFor="lastName" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2217">
                            Last Name
                          </label>
                          <input id="lastName" type="text" value={editedLastName} onChange={e => setEditedLastName(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="Last Name" data-id="element-2218" />
                        </div>
                        {/* Instrument and Grade - side by side */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2219">
                          <div data-id="element-2220">
                            <label htmlFor="instrument-edit" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2221">
                              Instrument / Lesson
                            </label>
                            <input id="instrument-edit" type="text" value={editedInstrument} onChange={e => setEditedInstrument(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="e.g., Piano, Guitar" data-id="element-2222" />
                          </div>
                          <div data-id="element-2223">
                            <label htmlFor="grade-edit" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2224">
                              Grade
                            </label>
                            <input id="grade-edit" type="number" value={editedGrade} onChange={e => setEditedGrade(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="1-12" min="1" max="12" data-id="element-2225" />
                          </div>
                        </div>
                      </div> : <>
                        <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} truncate mb-2`} data-id="element-2226">
                          {fullName}
                        </h3>
                        {/* Instrument and Grade display */}
                        <div className="flex items-center gap-2" data-id="element-2227">
                          {displayInstrument && <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' : 'bg-violet-500/10 text-violet-600 border border-violet-500/30'}`} data-id="element-2228">
                              <MusicIcon className="h-3 w-3 mr-1" data-id="element-2229" />
                              {displayInstrument}
                              {displayStudent.grade && ` · Grade ${displayStudent.grade}`}
                            </span>}
                        </div>
                      </>}
                  </div>
                </div>
              </div>
              {/* Contact & Basics Section */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2230">
                <div className="mb-4" data-id="element-2231">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2232">
                    Contact & Details
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-4" data-id="element-2233">
                    {/* Email Input */}
                    <div data-id="element-2234">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2235">
                        Email
                      </label>
                      <input type="email" value={editedEmail} onChange={handleEmailChange} onBlur={handleEmailBlur} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.email ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="student@example.com" data-id="element-2236" />
                      {validationErrors.email && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2237">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2238" />
                          {validationErrors.email}
                        </p>}
                    </div>
                    {/* Phone Input with formatting */}
                    <div data-id="element-2239">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2240">
                        Phone
                      </label>
                      <input ref={phoneInputRef} type="tel" value={editedPhone} onChange={handlePhoneChange} onBlur={handlePhoneBlur} placeholder="(555) 123-4567" maxLength={14} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.phone ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2241" />
                      {validationErrors.phone && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2242">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2243" />
                          {validationErrors.phone}
                        </p>}
                    </div>
                    {/* Time Zone Select */}
                    <div data-id="element-2244">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2245">
                        Time Zone
                      </label>
                      <select value={editedTimeZone} onChange={e => setEditedTimeZone(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2246">
                        <option value="" data-id="element-2247">Select time zone</option>
                        <option value="Eastern Time (ET)" data-id="element-2248">
                          Eastern Time (ET)
                        </option>
                        <option value="Central Time (CT)" data-id="element-2249">
                          Central Time (CT)
                        </option>
                        <option value="Mountain Time (MT)" data-id="element-2250">
                          Mountain Time (MT)
                        </option>
                        <option value="Pacific Time (PT)" data-id="element-2251">
                          Pacific Time (PT)
                        </option>
                        <option value="Alaska Time (AKT)" data-id="element-2252">
                          Alaska Time (AKT)
                        </option>
                        <option value="Hawaii-Aleutian Time (HST)" data-id="element-2253">
                          Hawaii-Aleutian Time (HST)
                        </option>
                      </select>
                    </div>
                  </div> : <div className="grid grid-cols-2 gap-4" data-id="element-2254">
                    {/* Email */}
                    <div className="space-y-1" data-id="element-2255">
                      <div className="flex items-center gap-2" data-id="element-2256">
                        <MailIcon className="h-4 w-4 text-blue-500" data-id="element-2257" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2258">
                          Email
                        </span>
                      </div>
                      <p className={`text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2259">
                        {displayStudent.email || 'Not provided'}
                      </p>
                    </div>
                    {/* Phone - display formatted */}
                    <div className="space-y-1" data-id="element-2260">
                      <div className="flex items-center gap-2" data-id="element-2261">
                        <PhoneIcon className="h-4 w-4 text-green-500" data-id="element-2262" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2263">
                          Phone
                        </span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2264">
                        {displayStudent.phone ? formatPhoneNumber(displayStudent.phone) : 'Not provided'}
                      </p>
                    </div>
                    {/* Time Zone */}
                    <div className="space-y-1" data-id="element-2265">
                      <div className="flex items-center gap-2" data-id="element-2266">
                        <ClockIcon className="h-4 w-4 text-orange-500" data-id="element-2267" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2268">
                          Time Zone
                        </span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2269">
                        {displayStudent.timeZone || 'Not specified'}
                      </p>
                    </div>
                  </div>}
              </div>
              {/* Address Section */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2270">
                <div className="mb-4" data-id="element-2271">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2272">
                    Address
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-4" data-id="element-2273">
                    {/* Street Address */}
                    <div data-id="element-2274">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2275">
                        Street Address
                      </label>
                      <input type="text" value={editedAddressStreet} onChange={e => setEditedAddressStreet(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2276" />
                    </div>
                    {/* Apartment/Unit */}
                    <div data-id="element-2277">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2278">
                        Apartment/Unit (Optional)
                      </label>
                      <input type="text" value={editedAddressApt} onChange={e => setEditedAddressApt(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2279" />
                    </div>
                    {/* City, State, ZIP in a grid */}
                    <div className="grid grid-cols-2 gap-3" data-id="element-2280">
                      {/* City */}
                      <div className="col-span-2" data-id="element-2281">
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2282">
                          City
                        </label>
                        <input type="text" value={editedAddressCity} onChange={e => setEditedAddressCity(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2283" />
                      </div>
                      {/* State */}
                      <div data-id="element-2284">
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2285">
                          State
                        </label>
                        <input type="text" value={editedAddressState} onChange={e => setEditedAddressState(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2286" />
                      </div>
                      {/* ZIP */}
                      <div data-id="element-2287">
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2288">
                          ZIP Code
                        </label>
                        <input type="text" value={editedAddressZip} onChange={e => setEditedAddressZip(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2289" />
                      </div>
                    </div>
                  </div> : <div data-id="element-2290">
                    {displayStudent.address && (displayStudent.address.street || displayStudent.address.city || displayStudent.address.state) ? <div className="space-y-1" data-id="element-2291">
                        <div className="flex items-center gap-2 mb-2" data-id="element-2292">
                          <MapPinIcon className="h-4 w-4 text-red-500" data-id="element-2293" />
                          <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2294">
                            Location
                          </span>
                        </div>
                        {displayStudent.address.street && <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2295">
                            {displayStudent.address.street}
                            {displayStudent.address.apt && `, ${displayStudent.address.apt}`}
                          </p>}
                        {(displayStudent.address.city || displayStudent.address.state) && <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2296">
                            {displayStudent.address.city}
                            {displayStudent.address.city && displayStudent.address.state && ', '}
                            {displayStudent.address.state}{' '}
                            {displayStudent.address.zip}
                          </p>}
                      </div> : <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2297">
                        No address provided
                      </p>}
                  </div>}
              </div>
              {/* Guardian/Parent Section */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2298">
                <div className="mb-4" data-id="element-2299">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2300">
                    Parent / Guardian
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-4" data-id="element-2301">
                    {/* Guardian Name */}
                    <div data-id="element-2302">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2303">
                        Guardian Name
                      </label>
                      <input type="text" value={editedGuardianName} onChange={e => setEditedGuardianName(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="Parent or Guardian Name" data-id="element-2304" />
                    </div>
                    {/* Guardian Email */}
                    <div data-id="element-2305">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2306">
                        Guardian Email
                      </label>
                      <input type="email" value={editedGuardianEmail} onChange={handleGuardianEmailChange} onBlur={handleGuardianEmailBlur} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.guardianEmail ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="guardian@example.com" data-id="element-2307" />
                      {validationErrors.guardianEmail && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2308">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2309" />
                          {validationErrors.guardianEmail}
                        </p>}
                    </div>
                    {/* Guardian Phone with formatting */}
                    <div data-id="element-2310">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2311">
                        Guardian Phone
                      </label>
                      <input ref={guardianPhoneInputRef} type="tel" value={editedGuardianPhone} onChange={handleGuardianPhoneChange} onBlur={handleGuardianPhoneBlur} placeholder="(555) 987-6543" maxLength={14} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.guardianPhone ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2312" />
                      {validationErrors.guardianPhone && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2313">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2314" />
                          {validationErrors.guardianPhone}
                        </p>}
                    </div>
                    {/* Preferred Contact Method */}
                    <div data-id="element-2315">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2316">
                        Preferred Contact Method
                      </label>
                      <select value={editedGuardianPreferredContact} onChange={e => setEditedGuardianPreferredContact(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2317">
                        <option value="" data-id="element-2318">Select method</option>
                        <option value="Email" data-id="element-2319">Email</option>
                        <option value="Phone" data-id="element-2320">Phone</option>
                        <option value="Text" data-id="element-2321">Text</option>
                      </select>
                    </div>
                    {/* Emergency Contact Checkbox */}
                    <div className="flex items-center" data-id="element-2322">
                      <input type="checkbox" id="emergencyContact" checked={editedGuardianIsEmergencyContact} onChange={e => setEditedGuardianIsEmergencyContact(e.target.checked)} className={`mr-2 h-4 w-4 rounded ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-emerald-500' : 'bg-white border-gray-300 text-deep-pink'} focus:ring-emerald-500`} data-id="element-2323" />
                      <label htmlFor="emergencyContact" className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2324">
                        Emergency Contact
                      </label>
                    </div>
                  </div> : <div data-id="element-2325">
                    {displayStudent.guardian && displayStudent.guardian.name ? <div className="space-y-3" data-id="element-2326">
                        <div className="flex items-center gap-2 mb-2" data-id="element-2327">
                          <UserIcon className="h-4 w-4 text-purple-500" data-id="element-2328" />
                          <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2329">
                            Guardian Information
                          </span>
                        </div>
                        {/* Guardian Name */}
                        <div data-id="element-2330">
                          <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2331">
                            {displayStudent.guardian.name}
                            {displayStudent.guardian.isEmergencyContact && <span className={`ml-2 text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2332">
                                (Emergency Contact)
                              </span>}
                          </p>
                        </div>
                        {/* Guardian Contact Info */}
                        <div className="grid grid-cols-2 gap-3 mt-2" data-id="element-2333">
                          {displayStudent.guardian.email && <div className="space-y-1" data-id="element-2334">
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2335">
                                Email
                              </span>
                              <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2336">
                                {displayStudent.guardian.email}
                              </p>
                            </div>}
                          {displayStudent.guardian.phone && <div className="space-y-1" data-id="element-2337">
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2338">
                                Phone
                              </span>
                              <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2339">
                                {formatPhoneNumber(displayStudent.guardian.phone)}
                              </p>
                            </div>}
                        </div>
                        {displayStudent.guardian.preferredContact && <div className="mt-2" data-id="element-2340">
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2341">
                              Preferred Contact:{' '}
                            </span>
                            <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2342">
                              {displayStudent.guardian.preferredContact}
                            </span>
                          </div>}
                      </div> : <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2343">
                        No guardian information provided
                      </p>}
                  </div>}
              </div>
              {/* Notes Section */}
              <div data-id="element-2344">
                <div className="mb-3" data-id="element-2345">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2346">
                    Notes
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-3" data-id="element-2347">
                    <textarea value={editedNotes} onChange={e => setEditedNotes(e.target.value)} rows={4} className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-emerald-500/50`} placeholder="Add notes about this student..." data-id="element-2348" />
                  </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-2349">
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2350">
                      {displayStudent.notes || 'No notes for this student.'}
                    </p>
                  </div>}
              </div>
            </div>
            {/* Footer Actions */}
            <div className={`px-6 py-4 border-t ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} flex justify-between`} data-id="element-2351">
              {isEditMode ? <div className="flex gap-2 ml-auto" data-id="element-2352">
                  <button onClick={handleCancelEdit} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${darkMode ? 'border-gray-400 text-gray-400 hover:bg-gray-400/10' : 'border-gray-500 text-gray-500 hover:bg-gray-50'}`} data-id="element-2353">
                    <XIcon className="h-4 w-4" data-id="element-2354" />
                    Cancel
                  </button>
                  <button onClick={handleSaveAll} disabled={validationErrors.email !== null || validationErrors.phone !== null || validationErrors.guardianEmail !== null || validationErrors.guardianPhone !== null} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${validationErrors.email !== null || validationErrors.phone !== null || validationErrors.guardianEmail !== null || validationErrors.guardianPhone !== null ? 'opacity-50 cursor-not-allowed border-gray-400 text-gray-400' : darkMode ? 'border-emerald-400 text-emerald-400 hover:bg-emerald-400/10' : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'}`} data-id="element-2355">
                    <SaveIcon className="h-4 w-4" data-id="element-2356" />
                    Save
                  </button>
                </div> : <>
                  <button onClick={handleDeleteClick} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${darkMode ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : 'border-red-500 text-red-600 hover:bg-red-50'}`} data-id="element-2357">
                    <Trash2Icon className="h-4 w-4" data-id="element-2358" />
                    Delete
                  </button>
                  <button onClick={handleEnterEditMode} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${darkMode ? 'border-emerald-400 text-emerald-400 hover:bg-emerald-400/10' : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'}`} data-id="element-2359">
                    <PencilIcon className="h-4 w-4" data-id="element-2360" />
                    Edit Profile
                  </button>
                </>}
            </div>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-2361">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!isDeleting ? handleDeleteCancel : undefined} data-id="element-2362" />
          {/* Modal */}
          <div className={`relative w-full max-w-md transform transition-all ${darkMode ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50' : 'bg-white border border-gray-200'} shadow-2xl rounded-2xl overflow-hidden`} data-id="element-2363">
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${darkMode ? 'border-navy-700/50' : 'border-gray-200'}`} data-id="element-2364">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2365">
                Delete Student Profile
              </h3>
            </div>
            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4" data-id="element-2366">
              <div className={`flex items-start gap-3 p-3 rounded-lg ${darkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`} data-id="element-2367">
                <AlertCircleIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2368" />
                <div data-id="element-2369">
                  <p className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-800'}`} data-id="element-2370">
                    This action cannot be undone
                  </p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-red-200/80' : 'text-red-700'}`} data-id="element-2371">
                    All student data, including lessons, notes, and progress
                    will be permanently deleted.
                  </p>
                </div>
              </div>
              <div data-id="element-2372">
                <label htmlFor="delete-password" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2373">
                  Enter your password to confirm
                </label>
                <input id="delete-password" type="password" value={deletePassword} onChange={e => {
              setDeletePassword(e.target.value);
              setPasswordError('');
            }} onKeyDown={handlePasswordKeyDown} placeholder="Your password" disabled={isDeleting} className={`w-full px-4 py-2 rounded-lg border transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-500 focus:border-red-500 focus:ring-1 focus:ring-red-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'} ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2374" />
                {passwordError && <p className={`mt-2 text-sm flex items-center gap-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2375">
                    <AlertCircleIcon className="h-4 w-4" data-id="element-2376" />
                    {passwordError}
                  </p>}
              </div>
            </div>
            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} flex justify-end gap-3`} data-id="element-2377">
              <button onClick={handleDeleteCancel} disabled={isDeleting} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2378">
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} disabled={isDeleting || !deletePassword.trim()} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'} ${isDeleting || !deletePassword.trim() ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2379">
                {isDeleting ? <span className="inline-flex items-center" data-id="element-2380">
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-2381">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2382"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2383"></path>
                    </svg>
                    Deleting...
                  </span> : 'Delete Student'}
              </button>
            </div>
          </div>
        </div>}
    </div>;
};